import React, { useState, useEffect } from 'react';
import { getUniversities, saveUniversity, deleteUniversity } from '../services/apiService';
import { supabase } from '../services/supabase';
import { useToast } from '../hooks/useToast';
import Toast from '../components/Toast';
import { Plus, Trash2, Edit2, Building2, Globe, MapPin, Award, X, Loader2 } from 'lucide-react';

const AdminPartneredUniversities = () => {
  const [universities, setUniversities] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const { toast, showToast, hideToast } = useToast();
  const [editingUniversity, setEditingUniversity] = useState(null);
  
  // Logic for Custom Country
  const [isCustomCountry, setIsCustomCountry] = useState(false);
  const PREDEFINED_COUNTRIES = ['USA', 'UK', 'Canada', 'Australia', 'Germany', 'New Zealand', 'Ireland', 'France'];

  const [formData, setFormData] = useState({
    name: '',
    country: 'USA',
    domain: '',
    rank: '',
    logo_url: null,
    logo_file: null,
  });

  useEffect(() => {
    loadUniversities();
  }, []);

  const loadUniversities = async () => {
    try {
      setIsLoading(true);
      const data = await getUniversities();
      setUniversities(data);
    } catch (error) {
      console.error('Error loading universities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCountrySelect = (e) => {
    const value = e.target.value;
    if (value === 'Other') {
      setIsCustomCountry(true);
      setFormData(prev => ({ ...prev, country: '' })); // Clear so they have to type
    } else {
      setIsCustomCountry(false);
      setFormData(prev => ({ ...prev, country: value }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        logo_file: file,
      });
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      let logoUrl = editingUniversity?.logo_url || editingUniversity?.logo;

      if (formData.logo_file) {
        const fileExt = formData.logo_file.name.split('.').pop();
        const fileName = `university_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `universities/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('photos')
          .upload(filePath, formData.logo_file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) {
          console.error('Logo upload error:', uploadError);
          showToast('Failed to upload logo. Please try again.', 'error');
          setSaving(false);
          return;
        }

        const { data: { publicUrl } } = supabase.storage.from('photos').getPublicUrl(filePath);
        logoUrl = publicUrl;
      }

      await saveUniversity({
        name: formData.name,
        country: formData.country, // This will contain the manual text if "Other" was used
        domain: formData.domain,
        rank: formData.rank,
        logo_url: logoUrl,
        id: editingUniversity?.id,
      });
      
      resetForm();
      loadUniversities();
      showToast(editingUniversity ? 'University updated!' : 'University added!', 'success');
    } catch (error) {
      console.error('Error saving university:', error);
      showToast('Error saving university. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingUniversity(null);
    setFormData({ name: '', country: 'USA', domain: '', rank: '', logo_url: null, logo_file: null });
    setPreviewUrl(null);
    setIsCustomCountry(false);
  };

  const handleEdit = (university) => {
    setEditingUniversity(university);
    
    // Check if the university country is in our predefined list
    const isPredefined = PREDEFINED_COUNTRIES.includes(university.country);
    
    setIsCustomCountry(!isPredefined);

    setFormData({
      name: university.name || '',
      country: university.country || 'USA',
      domain: university.domain || '',
      rank: university.rank || '',
      logo_url: null,
      logo_file: null,
    });
    setPreviewUrl(university.logo_url || university.logo || null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this university?')) {
      try {
        await deleteUniversity(id);
        loadUniversities();
        showToast('University deleted!', 'success');
      } catch (error) {
        console.error('Error deleting university:', error);
        showToast('Error deleting university. Please try again.', 'error');
      }
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">Partnered Universities</h1>
            <p className="text-slate-500 text-xs md:text-sm mt-1">Manage partner institutions.</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="w-full md:w-auto flex items-center justify-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20"
        >
          <Plus size={18} />
          Add University
        </button>
      </div>

      {/* Universities List */}
      {isLoading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-red-600 mb-4"></div>
            <p className="text-slate-500">Loading universities...</p>
        </div>
      ) : universities.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {universities.map((university) => (
            <div key={university.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden border border-slate-200 flex flex-col">
              <div className="p-4 md:p-6 flex-1">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                    {(() => {
                      const url = university.logo_url || university.logo;
                      const safeUrl = url && !url.startsWith('blob:') ? url : null;
                      return safeUrl ? (
                        <img
                          src={safeUrl}
                          alt={university.name}
                          className="w-full h-full object-contain p-1"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextElementSibling.style.display = 'flex';
                          }}
                        />
                      ) : null;
                    })()}
                    <div 
                      className="w-full h-full flex items-center justify-center bg-gradient-to-br from-red-100 to-red-200" 
                      style={{ display: (() => {
                        const url = university.logo_url || university.logo;
                        return url && !url.startsWith('blob:') ? 'none' : 'flex';
                      })() }}
                    >
                      <Building2 size={20} className="text-red-600 md:w-6 md:h-6" />
                    </div>
                  </div>
                  {university.rank && (
                    <span className="inline-flex items-center gap-1 bg-slate-50 border border-slate-100 px-2 py-1 rounded text-[10px] md:text-xs font-bold uppercase tracking-wider text-slate-500 ml-2 text-right">
                      <Award size={10} className="text-red-500 shrink-0" /> {university.rank}
                    </span>
                  )}
                </div>
                
                <h3 className="text-base md:text-lg font-bold text-slate-900 mb-2 line-clamp-2">{university.name}</h3>
                
                <div className="flex items-center gap-2 text-xs md:text-sm text-slate-600 mb-3">
                  <MapPin size={14} className="text-red-500 shrink-0" />
                  {university.country}
                </div>
                
                {university.domain && (
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-4 truncate">
                    <Globe size={12} className="shrink-0" />
                    <span className="truncate">{university.domain}</span>
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t border-slate-50 flex gap-2 mt-auto bg-slate-50/50">
                  <button
                    onClick={() => handleEdit(university)}
                    className="flex-1 bg-white border border-slate-200 text-slate-600 py-2 rounded-lg font-semibold hover:bg-slate-50 hover:text-blue-600 text-sm flex items-center justify-center gap-2 transition-colors"
                  >
                    <Edit2 size={14} /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(university.id)}
                    className="flex-1 bg-white border border-slate-200 text-slate-600 py-2 rounded-lg font-semibold hover:bg-red-50 hover:text-red-600 hover:border-red-100 text-sm flex items-center justify-center gap-2 transition-colors"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
            <Building2 size={32} />
          </div>
          <p className="text-lg font-bold text-slate-900">No universities yet</p>
          <p className="text-slate-500 text-sm mt-1">Add your first partner university.</p>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in slide-in-from-bottom md:zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
            
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50 sticky top-0 z-10">
              <h2 className="text-lg font-bold text-slate-900">
                {editingUniversity ? 'Edit University' : 'Add University'}
              </h2>
              <button onClick={resetForm} className="text-slate-400 hover:text-red-600 transition-colors">
                <X size={20} />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="overflow-y-auto p-6">
                <form id="universityForm" onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">University Name *</label>
                    <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm"
                    placeholder="e.g., Harvard University"
                    />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Country *</label>
                        
                        {/* 1. The Dropdown Selection */}
                        <select
                            name="country_select"
                            value={isCustomCountry ? 'Other' : formData.country}
                            onChange={handleCountrySelect}
                            className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm appearance-none mb-2"
                        >
                            {PREDEFINED_COUNTRIES.map((country) => (
                                <option key={country} value={country}>
                                    {country}
                                </option>
                            ))}
                            <option value="Other">Other (Enter Manually)</option>
                        </select>

                        {/* 2. The Conditional Text Input */}
                        {isCustomCountry && (
                            <input
                                type="text"
                                name="country"
                                required
                                value={formData.country}
                                onChange={handleChange}
                                placeholder="Enter Country Name"
                                className="w-full px-4 py-2 bg-white border border-red-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm animate-in fade-in slide-in-from-top-1"
                            />
                        )}
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Rank/Badge</label>
                        <input
                            type="text"
                            name="rank"
                            value={formData.rank}
                            onChange={handleChange}
                            className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm"
                            placeholder="e.g., #1 Global"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Domain/Website</label>
                    <input
                    type="text"
                    name="domain"
                    value={formData.domain}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:bg-white focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none transition-all text-sm"
                    placeholder="e.g., harvard.edu"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">Used for auto-fetching logos if available.</p>
                </div>

                <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Logo (Optional)</label>
                    <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm file:mr-4 file:py-1 file:px-3 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-slate-200 file:text-slate-700 hover:file:bg-slate-300"
                    />
                    {previewUrl && (
                    <div className="mt-4 p-2 border border-slate-100 rounded-lg inline-block bg-slate-50">
                        <img
                        src={previewUrl}
                        alt="Preview"
                        className="h-16 w-auto object-contain rounded"
                        />
                    </div>
                    )}
                </div>
                </form>
            </div>

            {/* Modal Footer - Fixed */}
            <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex gap-3">
                <button
                    type="button"
                    onClick={resetForm}
                    disabled={saving}
                    className="flex-1 py-2.5 rounded-lg font-medium text-slate-600 hover:bg-slate-100 transition-colors text-sm"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    form="universityForm"
                    disabled={saving}
                    className="flex-1 bg-red-600 text-white py-2.5 rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm flex items-center justify-center gap-2"
                >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : editingUniversity ? 'Update' : 'Add'}
                </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} duration={toast.duration} onClose={hideToast} />}
    </div>
  );
};

export default AdminPartneredUniversities;